import { useMutation, useQuery } from 'react-query';
import request from './request';
import { QUERY_RETRY, DATA_QUERY_EXPIRATION } from './queries';
import type { ModelType } from './models';

export interface KpiSource {
  value: string;
  label: string;
  // Connector source whose backfill hasn't settled yet — rendered non-selectable
  disabled?: boolean;
}

export interface KpiMetric {
  value: string;
  label: string;
}

// Note: the KPI Category concept was deliberately retired from the model
// forms (replaced by the explicit kpiUnit selector). The backend endpoint
// /data/kpi-categories still exists for the future sub-KPI/hierarchical
// models feature — re-add a client method intentionally when that lands.

// KPI metrics are dynamic — any numeric column of the customer's KPI table
// (the available set comes from /data/kpi-metrics)
export type KpiMetricValue = string;

export interface UserModel {
  modelName: string;
  kpiSource: string;
  kpiMetric: KpiMetricValue;
  kpiCategory: string | null;
  kpiUnit: 'euro' | 'unit';
  selectedMedia: string[];
  campaignSelections: Record<string, Record<string, boolean>>;
  modelType: ModelType;
}

export interface StartModelingRequest {
  customer: string;
  models: UserModel[];
}

class KpiDataService {
  async getSources(customer: string): Promise<KpiSource[]> {
    const response = await request.get('/data/kpi-sources', {
      params: { customer },
    });
    return response.data;
  }

  async getMetrics(customer: string, sourceId: string): Promise<KpiMetric[]> {
    const response = await request.get('/data/kpi-metrics', {
      params: { customer, source: sourceId },
    });
    return response.data;
  }
}

export const kpiDataService = new KpiDataService();

export function useStartModelingMutation() {
  return useMutation((data: StartModelingRequest) => {
    const { customer, models } = data;
    return request.post('/data/models', { models }, { params: { customer } });
  });
}

export function useKpiSourcesQuery(customer: string) {
  return useQuery(
    ['kpiSources', customer],
    () => kpiDataService.getSources(customer),
    {
      enabled: Boolean(customer),
      retry: QUERY_RETRY,
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
}
